// src/utils/employeePhoto.js
//
// ONE way to show an employee's profile photo.
//
// Owner (2026-10-01): "I don't want it to depend on the local PC or server."
// Photos live in the empPhotos table of the database (the row the org chart,
// the HR "Missing photos" count and HR "Change photo" use). The API serves it
// at GET /registration/employee-photo/{empId} with the sign-in, so an <img>
// cannot load it by a bare link: it is fetched here and shown as an object
// URL. Employees.ImagePath used to name a file on the web server; such a file
// is gone after a move or a redeploy, so it is only the LAST fallback (a
// shipped placeholder or test image), and a broken file falls back to the
// placeholder through onError.
//
// Usage:  const photo = useEmployeePhoto(empId, imagePath);
//         <img src={photo.src} onError={photo.onError} />
import { useEffect, useState } from "react";
import api from "../api";
import { assetUrl } from "./assetBase";

export const PLACEHOLDER_PHOTO = "/uploads/user-dummy-img.jpg";

// empId -> Promise<string|null>: one request per person per page load.
const cache = new Map();

const key = (empId) => String(empId ?? "").trim();

export function isPlaceholderPath(path) {
    return !path || /user-dummy-img|logofinal/i.test(String(path));
}

/** The database photo as an object URL, or null when the person has none. */
export function fetchEmployeePhoto(empId) {
    const id = key(empId);
    if (!id) return Promise.resolve(null);
    if (!cache.has(id)) {
        cache.set(id, api
            .get(`/registration/employee-photo/${encodeURIComponent(id)}`, { responseType: "blob" })
            .then((res) => (res?.data instanceof Blob && res.data.size > 0) ? URL.createObjectURL(res.data) : null)
            .catch(() => null));
    }
    return cache.get(id);
}

/** After HR uploads a new photo: show that file at once, everywhere on this page. */
export function rememberEmployeePhoto(empId, file) {
    const id = key(empId);
    if (!id) return null;
    const url = file instanceof Blob ? URL.createObjectURL(file) : null;
    if (url) cache.set(id, Promise.resolve(url));
    else cache.delete(id);
    return url;
}

/** The address to show before (or without) a database photo. */
export function fallbackPhotoUrl(imagePath) {
    return assetUrl(isPlaceholderPath(imagePath) ? PLACEHOLDER_PHOTO : imagePath);
}

export function useEmployeePhoto(empId, imagePath) {
    const fallback = fallbackPhotoUrl(imagePath);
    const [src, setSrc] = useState(fallback);
    const id = key(empId);

    useEffect(() => {
        let alive = true;
        setSrc(fallback);
        fetchEmployeePhoto(id).then((url) => { if (alive && url) setSrc(url); });
        return () => { alive = false; };
    }, [id, fallback]);

    const onError = (e) => {
        const placeholder = assetUrl(PLACEHOLDER_PHOTO);
        if (e?.target && e.target.src !== placeholder) e.target.src = placeholder;
    };

    return { src, onError };
}

export default useEmployeePhoto;
